"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import Icon from "@/components/icons";
import { BylineData } from "../types";
import sanitizeAndParseInlineHTML from "@/utils/functions/sanitizeAndParseInlineHTML";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import useTranslations from "@/i18n/useTranslations";

type BylinePopoverProps = {
  data: BylineData;
  prefix: string;
};

// Helper to limit <li> items in HTML string (for contributor data)
function limitListItems(html: string, maxItems: number = 2): string {
  const liMatches = html.match(/<li[^>]*>[\s\S]*?<\/li>/gi);
  if (!liMatches) return html;

  const limitedLis = liMatches.slice(0, maxItems).join("");
  // Wrap back in ul if original had ul
  if (html.includes("<ul")) {
    return `<ul>${limitedLis}</ul>`;
  }
  return limitedLis;
}

export default function BylinePopover({ data, prefix }: BylinePopoverProps) {
  const tDialog = useTranslations("Dialog");
  const tCommon = useTranslations("Common");

  const [isOpen, setIsOpen] = useState(false);
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const clearHideTimeout = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  };

  const showPopover = () => {
    clearHideTimeout();
    setIsOpen(true);
  };

  const hidePopover = () => {
    hideTimeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 150);
  };

  useEffect(() => {
    return () => clearHideTimeout();
  }, []);

  const getSocialIcon = (type: "linkedin" | "website") => {
    if (type === "linkedin") {
      return <Icon type="linkedin" svgProps={{ width: "20", height: "20" }} />;
    }
    return <Icon type="laptop" svgProps={{ width: "20", height: "20" }} />;
  };

  return (
    <span className="inline text-sm md:text-base">
      {prefix}{" "}
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="underline text-white hover:text-gray-200 cursor-pointer bg-transparent border-none p-0 font-inherit text-inherit"
            onMouseEnter={showPopover}
            onMouseLeave={hidePopover}
          >
            {data.name}
          </button>
        </PopoverTrigger>
        <PopoverContent
          side="bottom"
          align="center"
          sideOffset={8}
          onMouseEnter={showPopover}
          onMouseLeave={hidePopover}
          className="w-[320px] md:w-[380px] p-0 bg-white rounded-xl shadow-xl border-0"
        >
          <div className="mx-4 divide-y divide-gray-200">
            {/* Header */}
            <div className="flex items-start gap-3 py-4">
              {data.imageUrl && (
                <div className="flex-shrink-0 w-16 h-16 rounded-full overflow-hidden bg-gray-100">
                  <Image
                    src={data.imageUrl}
                    alt={data.name}
                    width={64}
                    height={64}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <Link
                  href={data.bioUrl}
                  className="underline font-semibold text-sm md:text-base block text-primary"
                >
                  {data.name}
                </Link>
                {data.role && (
                  <p className="text-primary line-clamp-1 text-sm md:text-base">
                    {data.role}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="flex-shrink-0 p-1 text-primary"
                aria-label={tDialog("close")}
              >
                <Icon
                  type="circle-close-fill"
                  svgProps={{ width: "20", height: "20" }}
                />
              </button>
            </div>

            {/* Body */}
            <div className="py-4">
              {data.expertise.length > 0 && (
                <div className="mb-3">
                  <section className="text-sm md:text-base font-semibold text-primary mb-2 flex items-center gap-1">
                    <Icon
                      type="circle-check"
                      svgProps={{ width: "24", height: "24" }}
                    />
                    <span>{tCommon("expertise")}</span>
                  </section>
                  {data.expertise.some(
                    (item) => item.includes("<ul") || item.includes("<li"),
                  ) ? (
                    <div className="text-primary space-y-1 [&_ul]:list-disc [&_ul]:list-inside pl-4 text-sm md:text-base">
                      {data.expertise.map((item, index) => (
                        <div key={index}>
                          {sanitizeAndParseInlineHTML(limitListItems(item, 2))}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <ul className="list-disc list-inside text-primary space-y-1 pl-4 text-sm md:text-base">
                      {data.expertise.slice(0, 2).map((item, index) => (
                        <li key={index}>{sanitizeAndParseInlineHTML(item)}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
              {data.about && (
                <div className="text-sm md:text-base text-primary line-clamp-4">
                  {sanitizeAndParseInlineHTML(data.about)}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between py-4 pt-6">
              <Link
                href={data.bioUrl}
                className="text-primary underline text-sm md:text-base"
              >
                {tCommon("readMore")}
              </Link>
              {data.socialLinks.length > 0 && (
                <div className="flex items-center gap-2">
                  {data.socialLinks.map((link, index) => (
                    <a
                      key={index}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:text-info-500 transition-colors"
                      aria-label={
                        link.type === "linkedin"
                          ? tCommon("linkedinProfile")
                          : tCommon("contributorWebsite")
                      }
                    >
                      {getSocialIcon(link.type)}
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </span>
  );
}
